/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ReactiveVar } from '@apollo/client';
import { OAuthAccessToken, OAuthTokenClient } from '@microsoft/esign.gateway.service.client';
import * as Teams from '@microsoft/teams-js';

import { useEffect, useState } from 'react';

import { Paths } from '../../../../common/enums/Paths';
import { ApprovalRequestType } from '../../../../data/types.generated';
import { LogCategory } from '../../../../logger';
import { AuthHelper } from '../../../../utilities/AuthHelper';
import { getEsignGatewayProvider } from '../../../../utilities/EsignUtils';
import { getAppHost } from '../../../../utilities/TeamsUtils';
import {
    getLocalStorageUserVariable,
    removeLocalStorageUserVariable,
    setLocalStorageUserVariable,
} from '../../../../utilities/Utils';
import { fetchAndLog, getEsignGatewayLoginConfiguration } from './EsignAuthHelpers';

const TOKEN_EXPIRATION_BUFFER = 300;
const docusignTokenCacheKey = 'docusign_oauth_token_v2';
export const adobeTokenCacheKey = 'adobe_oauth_token_v2';

type OAuthAccessTokenCached = OAuthAccessToken & { expirationDate: number };

export async function retrieveOAuthAccessToken(approvalRequestType: ApprovalRequestType): Promise<OAuthAccessToken> {
    const logger = getAppHost().getLogger();
    const logCategory = getLogCategory(approvalRequestType);

    logger.logTraceInfo(logCategory, 'Calling retrieveRequestDefaults');
    const cachedToken = getCachedToken(approvalRequestType);

    if (!isTokenExpired(cachedToken)) {
        return cachedToken;
    }

    if (isTokenExpired(cachedToken) && cachedToken && cachedToken.refreshTokenCipher) {
        logger.logTraceInfo(logCategory, 'Calling refresh token API');
        const refreshToken = await refreshAuthToken(approvalRequestType, cachedToken.refreshTokenCipher).catch(
            (error: any): OAuthAccessToken | null => {
                logger?.logTraceException(logCategory, error.message, {
                    error,
                    method: 'refreshAuthToken',
                });
                logger?.logTraceInfo(logCategory, 'Token expired, clearing local cache');
                return null;
            }
        );

        if (refreshToken) {
            // Refresh token doesn't contain apiBaseUrl and userId.
            const newToken: OAuthAccessToken = {
                ...cachedToken,
                accessToken: refreshToken.accessToken,
                expiresIn: refreshToken.expiresIn,
                refreshTokenCipher: refreshToken.refreshTokenCipher,
            };
            cacheAuthToken(approvalRequestType, newToken);
            logger.logTraceInfo(logCategory, 'Return refreshed token');
            return newToken;
        }

        clearCachedAuthToken(approvalRequestType);
        logger.logTraceInfo(logCategory, 'Unable to refresh token');
    }

    logger.logTraceInfo(logCategory, 'Retrieve token from Teams');
    const loginPath = getPathLoginStart(approvalRequestType);
    const loginStartUrl = window.location.origin + Paths.BasePath + loginPath;

    return AuthHelper.getAuthenticationFromTeams(loginStartUrl).then((token: string): OAuthAccessToken => {
        const oAuthToken = JSON.parse(token) as OAuthAccessToken;
        cacheAuthToken(approvalRequestType, oAuthToken);
        return oAuthToken;
    });
}

/**
 * Initialize the needsSignReactiveVar setting it to false if the user is already signed in.
 *
 * @param tokenCacheKey the key to use to retrieve the token in local storage
 * @param needsSignReactiveVar the reactive var that represents if the user needs to sign in
 * @param refreshAuthTokenRequest the function to call to request a new auth token from refresh token
 * @param clearCachedDefaults the function to call to clear the cached defaults
 * @return a boolean indicating that this function has finished its work
 */
export const useInitializeNeedsSignInReactiveVar = (
    approvalRequestType: ApprovalRequestType,
    needsSignReactiveVar: ReactiveVar<boolean>
): boolean => {
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect((): void => {
        const cachedToken = getCachedToken(approvalRequestType);

        if (!cachedToken) {
            setIsInitialized(true);
        } else if (!isTokenExpired(cachedToken)) {
            needsSignReactiveVar(false);
            setIsInitialized(true);
        } else if (isTokenExpired(cachedToken) && cachedToken && cachedToken.refreshTokenCipher) {
            refreshAuthToken(approvalRequestType, cachedToken.refreshTokenCipher)
                .then((token: OAuthAccessToken): void => {
                    if (token) {
                        const newToken: OAuthAccessToken = {
                            ...cachedToken,
                            accessToken: token.accessToken,
                            expiresIn: token.expiresIn,
                            refreshTokenCipher: token.refreshTokenCipher,
                        };
                        cacheAuthToken(approvalRequestType, newToken);
                        needsSignReactiveVar(false);
                    } else {
                        clearCachedAuthToken(approvalRequestType);
                    }
                })
                .finally((): void => setIsInitialized(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return isInitialized;
};

export const requestAuthToken = (
    approvalRequestType: ApprovalRequestType,
    clientCode: string,
    providerBaseUrlValidator?: (providerBaseUrl: string) => boolean
): void => {
    const { esign_token_error } = getAppHost().getLocale().strings;
    const logger = getAppHost().getLogger();
    const logCategory = getLogCategory(approvalRequestType);

    getOAuthToken(approvalRequestType, clientCode)
        .then((token: OAuthAccessToken): void => {
            if (token == null) {
                logger.logTraceError(logCategory, 'Empty AuthToken in response.');
                Teams.authentication.notifyFailure(esign_token_error);
            } else if (providerBaseUrlValidator && !providerBaseUrlValidator(token.apiBaseUrl)) {
                logger.logTraceError(logCategory, `Invalid API access point received from ${approvalRequestType}`);
                Teams.authentication.notifyFailure(esign_token_error);
            } else {
                const tokenString = JSON.stringify(token);
                Teams.authentication.notifySuccess(tokenString);
            }
        })
        .catch((error: any): void => {
            logger.logTraceException(logCategory, error.message, {
                error,
                method: 'fetchAuthToken',
            });
            Teams.authentication.notifyFailure(esign_token_error);
        });
};

const getOAuthToken = async (
    approvalRequestType: ApprovalRequestType,
    clientCode: string
): Promise<OAuthAccessToken> => {
    const configuration = await getEsignGatewayLoginConfiguration();
    const loginPath = getPathLoginEnd(approvalRequestType);
    const provider = getEsignGatewayProvider(approvalRequestType);
    return new OAuthTokenClient(configuration, null, { fetch: fetchAndLog }).getOAuthToken(provider, {
        clientCode,
        redirectUrl: window.location.origin + Paths.BasePath + loginPath,
    });
};

const refreshAuthToken = async (
    approvalRequestType: ApprovalRequestType,
    cypherText: string
): Promise<OAuthAccessToken> => {
    if (!cypherText) {
        return Promise.resolve(null);
    }

    const configuration = await getEsignGatewayLoginConfiguration();
    // Returning null is handled by the caller and will clear the cached tokens.
    const provider = getEsignGatewayProvider(approvalRequestType);
    return new OAuthTokenClient(configuration, null, { fetch: fetchAndLog }).refreshOAuthToken(provider, cypherText);
};

export const toExpirationDate = (expiresIn: number): number =>
    Date.now() + (expiresIn - TOKEN_EXPIRATION_BUFFER) * 1000;

export const clearCachedAuthToken = (approvalRequestType: ApprovalRequestType): void => {
    const cacheKey = getCacheKey(approvalRequestType);
    removeLocalStorageUserVariable(cacheKey);
};

export const getCachedToken = (approvalRequestType: ApprovalRequestType): OAuthAccessTokenCached | null => {
    const cacheKey = getCacheKey(approvalRequestType);
    const cachedValue = getLocalStorageUserVariable(cacheKey);
    return !!cachedValue ? JSON.parse(cachedValue) : null;
};

export const cacheAuthToken = (approvalRequestType: ApprovalRequestType, token: OAuthAccessToken): void => {
    const cacheKey = getCacheKey(approvalRequestType);
    // Add expiration date property to trigger refresh when needed.
    const tokenCached = { ...token, expirationDate: toExpirationDate(token.expiresIn) };
    setLocalStorageUserVariable(cacheKey, JSON.stringify(tokenCached));
};

const isTokenExpired = (token: OAuthAccessTokenCached): boolean => {
    return !token || (token.expirationDate && token.expirationDate <= Date.now());
};

const getCacheKey = (approvalRequestType: ApprovalRequestType): string =>
    approvalRequestType === ApprovalRequestType.Docusign ? docusignTokenCacheKey : adobeTokenCacheKey;

const getPathLoginStart = (approvalRequestType: ApprovalRequestType): string =>
    approvalRequestType === ApprovalRequestType.Docusign ? Paths.DocusignLoginStart : Paths.AdobeLoginStart;

const getPathLoginEnd = (approvalRequestType: ApprovalRequestType): string =>
    approvalRequestType === ApprovalRequestType.Docusign ? Paths.DocusignLoginEnd : Paths.AdobeLoginEnd;

const getLogCategory = (approvalRequestType: ApprovalRequestType): LogCategory =>
    approvalRequestType === ApprovalRequestType.Docusign ? LogCategory.DocusignAuth : LogCategory.AdobeAuth;
